import React from "react";

import { Box } from "@material-ui/core";

import HexLevel10Map, { MapCell, pointerInfoEmpty } from "components/visuals/maps/HexLevel10Map";
import useColourPalette from "components/visuals/useColourPalette";

import Legend from "./Legend";

import { selectIsLoading, selectHasErrors, selectFootfallByAreaMeasure, selectFootfallByAreaWithColourGroups, selectFootfallByAreaMapThresholds } from "modules/customer/insights/portfolioNew/footfallNew/footfallNewSlice";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";
import { useAppSelector } from "store";
import { selectStore } from "modules/customer/insights/portfolioNew/portfolioSlice";
import SelectedStoreMarker from "components/visuals/maps/SelectedStoreMarker";
import { FootfallMeasureType } from "modules/customer/insights/portfolioNew/footfallNew/footfallMeasureType";

const Map: React.FC = () => {
    const colourPalette = useColourPalette();
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const cells = useAppSelector(selectFootfallByAreaWithColourGroups);
    const thresholds = useAppSelector(selectFootfallByAreaMapThresholds);
    const measureType = useAppSelector(selectFootfallByAreaMeasure);
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const selectedStore = useAppSelector(selectStore);
    const [hoverInfo, setHoverInfo] = React.useState(pointerInfoEmpty);
    const hoveredCell = cells.find(cell => cell.cellId === hoverInfo.cellId);

    const [initialViewport, setInitialViewport] = React.useState({
        latitude: selectedStore?.latitude ?? 0,
        longitude: selectedStore?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    const mapCells: MapCell[] = React.useMemo(() => {
        return cells.map(cell => {
            return {
                id: String(cell.cellId),
                colourGroup: measureType === FootfallMeasureType.NumberOfVisits ? cell.visitsColourGroup : cell.dwellColourGroup,
            };
        });
    }, [cells, measureType]);

    const relevantThresholds: number[] = React.useMemo(() => {
        return measureType === FootfallMeasureType.NumberOfVisits ? thresholds.visitsThresholds : thresholds.dwellThresholds;
    }, [thresholds, measureType]);

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: selectedStore?.latitude ?? 0,
                longitude: selectedStore?.longitude ?? 0
            };
        });
    }, [selectedStore]);

    return (
        <Box data-cy="footfall-map">
            <HexLevel10Map
                loading={isLoading}
                error={hasErrors}
                title={`${selectedStore?.name} total visits and average dwell time in the last 12 months`}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                mapboxHexLevel10TilesetId={mapboxConfiguration.hex10TilesetId}
                mapboxHexLevel10TilesetUrl={mapboxConfiguration.hex10TilesetUrl}
                initialViewport={initialViewport}
                addGeocoder={false}
                cells={mapCells}
                colours={colourPalette.sequential}
                enableHover={true}
                onHover={setHoverInfo}
                enableClick={false}
                displayPOIs={true}
                downloadData={cells}
            >
                <Legend cell={hoveredCell} measureType={measureType} thresholds={relevantThresholds} />
                <SelectedStoreMarker latitude={selectedStore?.latitude ?? 0} longitude={selectedStore?.longitude ?? 0} />
            </HexLevel10Map>
        </Box>
    );
};


export default Map;
